import { useId, useState } from 'react';

export interface GameweekSeries {
  /** Identifies the series — an FPL entry id for a watched manager, an FPL
   * element id for a player (see `PlayerComparison`). String or number
   * either way, since this chart never does arithmetic on it, only React
   * keys and the hover/legend lookups below. */
  id: string | number;
  label: string;
  color: string;
  /** Ascending by gameweek; gaps are allowed (a gameweek never refreshed for
   * this manager is simply absent, not zero). */
  points: Array<{ gameweek: number; points: number }>;
}

interface GameweekChartProps {
  series: GameweekSeries[];
}

const WIDTH = 640;
const HEIGHT = 320;
const MARGIN = { top: 16, right: 16, bottom: 32, left: 40 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

const Y_TICK_COUNT = 4;

/** The smallest whole-number step, from a fixed set of round candidates
 * (1/2/5/10/20/25/50/100…), that's at least `roughStep` — always a whole
 * number so ticks built from it (`step`, `2*step`, `3*step`, …) never need
 * rounding for display, unlike dividing the max into equal fractions
 * (5 / 4 = 1.25, whose multiples aren't whole numbers). */
function niceStep(roughStep: number): number {
  const rounded = Math.max(1, roughStep);
  const magnitude = 10 ** Math.floor(Math.log10(rounded));
  for (const base of [1, 2, 5, 10]) {
    const candidate = base * magnitude;
    if (candidate >= rounded) return candidate;
  }
  return 10 * magnitude;
}

/**
 * A hand-rolled SVG line chart: gameweek along the bottom, points up the
 * side, one line per watched manager in a fixed colour order (never cycled
 * — see the dataviz skill). No charting library, matching this app's
 * "hand-rolled SVG, no dependency" convention elsewhere.
 *
 * Ships with a hover crosshair (dataviz's default interaction layer for a
 * line chart), a colour-keyed legend (a legend is mandatory for ≥2 series —
 * identity is never colour-alone), and a visually-hidden data table so the
 * same numbers are available to a screen reader.
 */
export function GameweekChart({ series }: GameweekChartProps) {
  const titleId = useId();
  const [hoverGameweek, setHoverGameweek] = useState<number | null>(null);

  const gameweeks = [...new Set(series.flatMap((s) => s.points.map((p) => p.gameweek)))].sort((a, b) => a - b);
  const maxGameweek = Math.max(1, ...gameweeks);
  const rawMaxPoints = Math.max(1, ...series.flatMap((s) => s.points.map((p) => p.points)));
  const yStep = niceStep(rawMaxPoints / Y_TICK_COUNT);
  const maxPoints = yStep * Math.ceil(rawMaxPoints / yStep);

  const xFor = (gameweek: number) => (maxGameweek === 1 ? 0 : ((gameweek - 1) / (maxGameweek - 1)) * PLOT_WIDTH);
  const yFor = (points: number) => PLOT_HEIGHT - (points / maxPoints) * PLOT_HEIGHT;

  // Skip labels once there'd be too many to read — every gameweek up to 10,
  // then thin them out rather than crowd every integer along the bottom.
  const tickEvery = maxGameweek <= 10 ? 1 : Math.ceil(maxGameweek / 10);
  const xTicks = Array.from({ length: maxGameweek }, (_, i) => i + 1).filter((gw) => gw % tickEvery === 0 || gw === 1);
  const yTicks = Array.from({ length: maxPoints / yStep + 1 }, (_, i) => i * yStep);

  function onMove(event: React.MouseEvent<SVGSVGElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * WIDTH - MARGIN.left;
    const gameweek = Math.round((x / PLOT_WIDTH) * (maxGameweek - 1)) + 1;
    setHoverGameweek(gameweek >= 1 && gameweek <= maxGameweek ? gameweek : null);
  }

  const hoveredReadings = hoverGameweek
    ? series
        .map((s) => ({ series: s, point: s.points.find((p) => p.gameweek === hoverGameweek) }))
        .filter((r): r is { series: GameweekSeries; point: { gameweek: number; points: number } } => r.point !== undefined)
    : [];

  return (
    <div className="gameweek-chart">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-labelledby={titleId}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverGameweek(null)}
      >
        <title id={titleId}>Points by gameweek for each watched manager</title>
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {yTicks.map((tick) => (
            <g key={tick}>
              <line x1={0} x2={PLOT_WIDTH} y1={yFor(tick)} y2={yFor(tick)} className="gameweek-chart-gridline" />
              <text x={-8} y={yFor(tick)} className="gameweek-chart-axis-label" textAnchor="end" dominantBaseline="middle">
                {Math.round(tick)}
              </text>
            </g>
          ))}

          {xTicks.map((gw) => (
            <text
              key={gw}
              x={xFor(gw)}
              y={PLOT_HEIGHT + 20}
              className="gameweek-chart-axis-label"
              textAnchor="middle"
            >
              {gw}
            </text>
          ))}
          <text x={PLOT_WIDTH / 2} y={PLOT_HEIGHT + 30} className="gameweek-chart-axis-title" textAnchor="middle">
            Gameweek
          </text>

          {hoverGameweek !== null && (
            <line
              x1={xFor(hoverGameweek)}
              x2={xFor(hoverGameweek)}
              y1={0}
              y2={PLOT_HEIGHT}
              className="gameweek-chart-crosshair"
            />
          )}

          {series.map((s) => {
            const path = s.points.map((p) => `${xFor(p.gameweek)},${yFor(p.points)}`).join(' L ');
            return (
              <g key={s.id}>
                {path && <path d={`M ${path}`} fill="none" stroke={s.color} strokeWidth={2} />}
                {s.points.map((p) => (
                  <circle
                    key={p.gameweek}
                    cx={xFor(p.gameweek)}
                    cy={yFor(p.points)}
                    r={p.gameweek === hoverGameweek ? 5 : 3}
                    fill={s.color}
                  />
                ))}
              </g>
            );
          })}
        </g>
      </svg>

      {hoveredReadings.length > 0 && (
        <div className="gameweek-chart-tooltip" role="status">
          <strong>Gameweek {hoverGameweek}</strong>
          <ul>
            {hoveredReadings.map(({ series: s, point }) => (
              <li key={s.id}>
                <span className="gameweek-chart-swatch" style={{ background: s.color }} aria-hidden="true" />
                {s.label}: {point.points}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className="gameweek-chart-legend">
        {series.map((s) => (
          <li key={s.id}>
            <span className="gameweek-chart-swatch" style={{ background: s.color }} aria-hidden="true" />
            {s.label}
          </li>
        ))}
      </ul>

      <table className="sr-only">
        <caption>Points by gameweek for each watched manager</caption>
        <thead>
          <tr>
            <th scope="col">Manager</th>
            <th scope="col">Gameweek</th>
            <th scope="col">Points</th>
          </tr>
        </thead>
        <tbody>
          {series.flatMap((s) =>
            s.points.map((p) => (
              <tr key={`${s.id}-${p.gameweek}`}>
                <td>{s.label}</td>
                <td>{p.gameweek}</td>
                <td>{p.points}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  );
}
